import React, { useState, useEffect, useMemo } from 'react';
import apiClient from '../utils/apiClient';
import Select from 'react-select';
import { SERVER_BASE } from '../config/server';
import { useTable, useSortBy, usePagination, useGlobalFilter } from 'react-table';
import 'bootstrap/dist/css/bootstrap.min.css';
import Swal from 'sweetalert2';
import moment from 'moment';
import DateRangePickerComponent from './CustomDateRangePicker';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const DepartmentItemAnalysisReport = ({ accessOfDepartments }) => {
    const [departments, setDepartments] = useState([]);
    const [selectedDept, setSelectedDept] = useState();
    const [selectedItem, setSelectedItem] = useState(null);
    const [itemList, setItemList] = useState([]);
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dateRange, setDateRange] = useState([null, null]);
    const [expandedRows, setExpandedRows] = useState({});
    const token = localStorage.getItem('token');
    const code = localStorage.getItem('code');
    useEffect(() => {
        if (accessOfDepartments) {
            const deptArray = accessOfDepartments
                .split(',')
                .map((dept) => ({ value: dept.trim(), label: dept.trim() }));
            setDepartments(deptArray);
        }
    }, [accessOfDepartments]);
    const fetchItemOptions = async (accessOfDepartmentsArray) => {
        try {
            const response = await apiClient.get(`${SERVER_BASE}/getItemOptionsByDepartment`, {
                params: {
                    code,
                    accessOfDepartments: accessOfDepartmentsArray.join(','),
                },
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setItemList(response.data.map((itm) => ({
                value: itm.id,
                label: itm.printName,
                uom: itm.uom,
            })));
        } catch (error) {
            console.error("Error fetching items:", error);
        }
    };

    const handleGetData = async () => {
        const [startDate, endDate] = dateRange;

        if (!startDate || !endDate) {
            Swal.fire({
                icon: 'warning',
                title: 'Oops...',
                text: 'Please select a valid date range!',
                position: 'top-end',
                showConfirmButton: false,
                timer: 1500,
                toast: true,
            });
            return;
        }
        if (!selectedDept) {
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'warning',
                title: 'Please select department',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true,
            });
            return;
        }
        if (!selectedItem) {
            Swal.fire({
                icon: 'warning',
                title: 'Oops...',
                text: 'Please select inventory name!',
                position: 'top-end',
                showConfirmButton: false,
                timer: 1500,
                toast: true,
            });
            setReportData([]);
            return;
        }

        setLoading(true);

        try {
            const response = await apiClient.get(`${SERVER_BASE}/getDepartmentItemAnalysis`, {
                params: {
                    itemId: selectedItem.value,
                    startDate: moment(startDate).format('YYYY-MM-DD'),
                    endDate: moment(endDate).format('YYYY-MM-DD'),
                    department: selectedDept?.value
                },
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            setReportData(response.data || []);
        } catch (error) {
            console.error("Error fetching item inventory report:", error);
            setReportData([]);
        } finally {
            setLoading(false);
        }
    };

    const columns = useMemo(() => [
        {
            Header: "S.No",
            Cell: ({ row }) => row.index + 1,
        },
        {
            Header: 'Location',
            accessor: 'location',
            Cell: ({ row }) => {
                const isExpanded = expandedRows[row.index];
                const hasStatus = row.original.statusWise?.length > 0;

                return (
                    <span
                        onClick={() => {
                            if (hasStatus) {
                                setExpandedRows(prev => ({
                                    ...prev,
                                    [row.index]: !prev[row.index],
                                }));
                            }
                        }}
                        style={{ cursor: hasStatus ? 'pointer' : 'default', color: hasStatus ? '#007bff' : 'inherit' }}
                    >
                        {hasStatus ? (isExpanded ? '▼ ' : '▶ ') : ''}{row.original.location}
                    </span>
                );
            }
        },
        {
            Header: 'Opening',
            accessor: 'opening',
            Cell: ({ value }) =>
                value == null ? '' : Number(value) % 1 === 0 ? Number(value) : Number(value).toFixed(2),
        },
        {
            Header: 'Addition',
            accessor: 'totalAdd',
            Cell: ({ value }) =>
                value == null ? '' : Number(value) % 1 === 0 ? Number(value) : Number(value).toFixed(2),
        },
        {
            Header: 'Deduction',
            accessor: 'totalLess',
            Cell: ({ value }) =>
                value == null ? '' : Number(value) % 1 === 0 ? Number(value) : Number(value).toFixed(2),
        },
        {
            Header: 'Closing',
            accessor: 'closing',
            Cell: ({ value }) =>
                value == null ? '' : Number(value) % 1 === 0 ? Number(value) : Number(value).toFixed(2),
        },
        {
            Header: 'UOM',
            accessor: 'uom',
            Cell: () => selectedItem?.uom || '',
        },
    ], [selectedItem, expandedRows]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        state: { pageIndex, globalFilter },
        canPreviousPage,
        canNextPage,
        pageOptions,
        nextPage,
        previousPage,
        setGlobalFilter,
    } = useTable(
        {
            columns,
            data: reportData || [],
            initialState: { pageIndex: 0 },
        },
        useGlobalFilter,
        useSortBy,
        usePagination
    );

    const exportToExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Item Analysis');

        // 1. Add "Item" row
        worksheet.addRow([`Department: ${selectedDept?.label || ''}`]);
        worksheet.addRow([`Item: ${selectedItem?.label || ''}`]);

        // 2. Add "Date Range" row
        const formattedStart = moment(dateRange[0]).format('DD-MMM-YYYY');
        const formattedEnd = moment(dateRange[1]).format('DD-MMM-YYYY');
        worksheet.addRow([`Date Range: ${formattedStart} to ${formattedEnd}`]);

        // 3. Add empty spacer row
        worksheet.addRow([]);

        // 4. Add table headers manually
        const headers = ['Location', 'Opening', 'Addition', 'Deduction', 'Closing', 'UOM'];
        const headerRow = worksheet.addRow(headers);
        headerRow.font = { bold: true };

        // 5. Add data rows
        reportData.forEach(row => {
            worksheet.addRow([
                row.location || '',
                row.opening || '',
                row.totalAdd || '',
                row.totalLess || '',
                row.closing || '',
                selectedItem?.uom || ''
            ]);

            if (row.statusWise?.length) {
                row.statusWise.forEach(s => {
                    worksheet.addRow([
                        `   └ ${s.status}`,
                        s.opening || '',
                        s.totalAdd || '',
                        s.totalLess || '',
                        s.closing || '',
                        selectedItem?.uom || ''
                    ]);
                });
            }
        });

        worksheet.columns = [
            { width: 25 }, { width: 15 }, { width: 15 }, { width: 15 }, { width: 15 }, { width: 10 }
        ];

        // 7. Generate and trigger download
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        saveAs(blob, `Department_Item_Analysis_${selectedItem?.label || 'Report'}.xlsx`);
    };

    return (
        <div className="formcontainer mt-3">
            <h5>Department Item Analysis</h5>
            <div className="row align-items-end mb-4">
                <div className="col-md-3">
                    <label className="form-label">Department</label>
                    <Select
                        options={departments}
                        value={selectedDept}
                        onChange={(selectedOption) => {
                            setSelectedDept(selectedOption);
                            if (selectedOption) {
                                fetchItemOptions([selectedOption.value]); // Wrap in array if your API expects multiple departments
                            }
                        }}
                    />
                </div>

                <div className="col-md-3">
                    <label className="form-label">Inventory Name</label>
                    <Select
                        options={itemList}
                        value={selectedItem}
                        onChange={setSelectedItem}
                        isClearable
                    />
                </div>

                <div className="col-md-3">
                    <label>Date Range:</label>
                    <DateRangePickerComponent value={dateRange} onChange={setDateRange} />
                </div>

                <div className="col-md-3 d-flex gap-2">
                    <button
                        onClick={handleGetData}
                        disabled={loading}
                        className="btn btn-outline-success w-50"
                    >
                        {loading ? 'Loading...' : 'Get Report'}
                    </button>

                    {reportData.length > 0 && (
                        <button
                            onClick={exportToExcel}
                            className="btn btn-outline-secondary w-50"
                        >
                            Export to Excel
                        </button>
                    )}
                </div>
            </div>

            <div className="col-md-12 mb-4">
                <input
                    type="text"
                    className="form-control"
                    placeholder="Search location"
                    value={globalFilter || ''}
                    onChange={(e) => setGlobalFilter(e.target.value)}
                />
            </div>
            {reportData.length > 0 && (
                <div className="mb-3 d-flex gap-2">
                    <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => {
                            const allExpanded = Object.keys(expandedRows).length === reportData.length;
                            if (allExpanded) {
                                setExpandedRows({});
                            } else {
                                const expandedMap = {};
                                reportData.forEach((_, index) => {
                                    expandedMap[index] = true;
                                });
                                setExpandedRows(expandedMap);
                            }
                        }}
                    >
                        {Object.keys(expandedRows).length === reportData.length ? 'Collapse All' : 'Expand All'}
                    </button>
                </div>
            )}

            <div className="table-responsive">
                <table
                    style={{ fontSize: '15px', verticalAlign: 'baseline' }}
                    className="table table-striped table-bordered"
                    {...getTableProps()}
                >
                    <thead>
                        {headerGroups.map(headerGroup => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map(column => (
                                    <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                                        {column.render('Header')}
                                        <span>
                                            {column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : ''}
                                        </span>
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                        {rows.map((row, i) => {
                            prepareRow(row);
                            const isExpanded = expandedRows[row.index];
                            const statusWise = row.original.statusWise || [];
                            return (
                                <React.Fragment key={i}>
                                    <tr {...row.getRowProps()} style={{ fontWeight: 500 }}>
                                        {row.cells.map(cell => (
                                            <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                                        ))}
                                    </tr>
                                    {isExpanded && statusWise.length > 0 && statusWise.map((s, idx) => (
                                        <tr
                                            key={`${row.index}-sub-${idx}`}
                                            style={{
                                                backgroundColor: '#e6f4ff', // Light blue background
                                                fontSize: '0.85rem',
                                                height: '28px',             // Reduced height
                                                lineHeight: '1.2',
                                            }}
                                        >
                                            {/* Empty cell for S.No alignment */}
                                            <td></td>

                                            <td style={{ paddingLeft: '80px', fontStyle: 'italic' }}>
                                                ↳ {s.status}
                                            </td>
                                            <td style={{ fontStyle: 'italic' }}>
                                                {Number(s.opening) % 1 === 0 ? Number(s.opening) : Number(s.opening).toFixed(2)}
                                            </td>
                                            <td style={{ fontStyle: 'italic' }}>
                                                {Number(s.totalAdd) % 1 === 0 ? Number(s.totalAdd) : Number(s.totalAdd).toFixed(2)}
                                            </td>
                                            <td style={{ fontStyle: 'italic' }}>
                                                {Number(s.totalLess) % 1 === 0 ? Number(s.totalLess) : Number(s.totalLess).toFixed(2)}
                                            </td>
                                            <td style={{ fontStyle: 'italic' }}>
                                                {Number(s.closing) % 1 === 0 ? Number(s.closing) : Number(s.closing).toFixed(2)}
                                            </td>
                                            <td style={{ fontStyle: 'italic' }}>
                                                {/* UOM left empty intentionally */}
                                            </td>
                                        </tr>
                                    ))}



                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
                <div className="d-flex justify-content-between align-items-center mt-3">
                    <button
                        className="btn btn-secondary me-2"
                        onClick={() => previousPage()}
                        disabled={!canPreviousPage}
                    >
                        Previous
                    </button>
                    <span>
                        Page {pageIndex + 1} of {pageOptions.length}
                    </span>
                    <button
                        className="btn btn-secondary"
                        onClick={() => nextPage()}
                        disabled={!canNextPage}
                    >
                        Next
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DepartmentItemAnalysisReport;
